import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Error } from "@material-ui/icons";
import { Table } from "antd";
import { ForecastMonthField } from "constants/forecasts.constants";
import { ICheckedForecast } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { ExportFormat, exportToCSV, exportToExcel } from "utils/exportUtils";

import { useUser } from "hooks";

import { useFetchForecastArray } from "components/arps/hooks/useFetchForecastArray";
import {
  IForecastMonth,
  generateForecastArrayFromArps
} from "components/arps/utils/forecastArrayUtils";
import { ExportButton } from "components/charts/toggles/data-table/ExportButton";

interface ForecastArrayContentProps {
  selectedTw: ICheckedForecast;
  isActive?: boolean;
}

const roundText = (text: string) => {
  if (!text) {
    return "";
  }
  return <span>{parseFloat(text).toFixed(1)}</span>;
};
const forecastArrayColumns = [
  {
    title: "Date",
    dataIndex: "prodDate",
    width: 120,
    render: (text: string) => {
      const date = new Date(text);
      return (
        <span>{`${date.getUTCFullYear()}-${(date.getUTCMonth() + 1)
          .toString()
          .padStart(2, "0")}-${date.getUTCDate().toString().padStart(2, "0")}`}</span>
      );
    }
  },
  {
    title: "Oil (bbl)",
    dataIndex: ForecastMonthField.OilBbl,
    render: roundText
  },
  {
    title: "Condensate (bbl)",
    dataIndex: ForecastMonthField.CondBbl,
    render: roundText
  },
  {
    title: "Gas (mcf)",
    dataIndex: ForecastMonthField.GasMcf,
    render: roundText
  },
  {
    title: "Water (bbl)",
    dataIndex: ForecastMonthField.WaterBbl,
    render: roundText
  }
];

export const ForecastArrayContent: FC<ForecastArrayContentProps> = ({
  selectedTw,
  isActive
}) => {
  const { data } = useFetchForecastArray({ ids: [selectedTw?.id] });
  const { hasTrialAccount, isReadonly } = useUser();
  const canExport = !hasTrialAccount && !isReadonly;
  const [forecastArray, setForecastArray] = useState<IForecastMonth[]>([]);

  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const normalizeTypeWell = useSelector(
    (state: RootState) => state.app.normalizeTypeWell
  );

  const [exportError, setExportError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = (format: ExportFormat) => {
    try {
      setIsExporting(true);
      setExportError(null);

      const headers = forecastArrayColumns.map((col) => ({
        key: col.dataIndex as string,
        label: col.title as string
      }));

      const title = selectedTw.title;

      if (format === ExportFormat.CSV) {
        exportToCSV(forecastArray, headers, title, title);
      } else {
        exportToExcel(forecastArray, headers, title, title);
      }
    } catch (e) {
      setExportError("Failed to export forecast array");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (!selectedTw) {
      setForecastArray([]);
      return;
    }

    if (
      data &&
      selectedTw.id in data.forecastArray &&
      data.forecastArray[selectedTw.id].length > 0
    ) {
      setForecastArray(data.forecastArray[selectedTw.id] as unknown as IForecastMonth[]);
    } else {
      const generated = generateForecastArrayFromArps(
        selectedTw,
        globalNormalizeBy,
        useNormalizeBy,
        normalizeTypeWell
      );
      setForecastArray(generated);
    }
  }, [data, selectedTw, globalNormalizeBy, useNormalizeBy, normalizeTypeWell, isActive]);

  return (
    <ForecastArrayContentRoot data-testid={"forecast-array-content-root"}>
      <TableWrapper>
        <Table
          dataSource={forecastArray}
          columns={forecastArrayColumns}
          pagination={false}
          rowKey="prodDate"
          scroll={{ y: "100%" }}
        />
      </TableWrapper>
      <ActionsWrapper>
        {exportError && (
          <ErrorContainer>
            <Error style={{ fontSize: 16 }} /> {exportError}
          </ErrorContainer>
        )}
        {canExport && <ExportButton exporting={isExporting} onExport={handleExport} />}
      </ActionsWrapper>
    </ForecastArrayContentRoot>
  );
};

const ForecastArrayContentRoot = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 5px 16px;
  gap: 10px;
  flex-shrink: 0;
  background: #f2f3f4; //rgba(var(--color-text-rgb), 0.05) solid color
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

const ErrorContainer = styled.div`
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  min-height: 0;

  .ant-table-thead > tr > th {
    background: #f2f3f4;
  }

  .ant-table-cell-scrollbar:not([rowspan]) {
    box-shadow: 0 1px 0 1px #f2f3f4;
  }

  .ant-table-wrapper,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table {
    height: 100%;
  }

  .ant-table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-table-body {
    flex: 1;
    min-height: 0;
    height: auto !important;
  }
`;
